<template>
  <!-- Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="material-icons-round me-2" style="vertical-align: middle;">folder_special</i>
            문제은행 파일 관리
            <span class="badge bg-gradient-primary ms-2">이미지 & 오디오</span>
          </h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="container-fluid">
            <!-- 탭 네비게이션 -->
            <ul class="nav nav-tabs mb-4" id="examFilesTabs" role="tablist">
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: activeTab === 'images' }"
                  @click="activeTab = 'images'"
                  type="button"
                >
                  <i class="material-icons-round me-1 text-sm">image</i>
                  이미지 파일
                  <span v-if="images.length > 0" class="badge bg-info ms-2">{{ images.length }}</span>
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: activeTab === 'audio' }"
                  @click="activeTab = 'audio'"
                  type="button"
                >
                  <i class="material-icons-round me-1 text-sm">audiotrack</i>
                  오디오 파일
                  <span v-if="audioFiles.length > 0" class="badge bg-info ms-2">{{ audioFiles.length }}</span>
                </button>
              </li>
            </ul>

            <!-- 이미지 탭 -->
            <div v-show="activeTab === 'images'">
              <!-- 이미지 업로드 섹션 -->
              <div class="upload-section mb-3">
                <div class="upload-header">
                  <div class="upload-content">
                    <h6 class="upload-title">이미지 업로드 (JPG, PNG, GIF, WebP 지원)</h6>
                  </div>
                </div>
                
                <div class="upload-form">
                  <!-- 첫 번째 줄: 파일 선택 + 업로드 버튼 -->
                  <div class="row g-2 align-items-center mb-2">
                    <div class="col-md-8">
                      <input 
                        type="file" 
                        ref="fileInput" 
                        multiple 
                        accept="image/*" 
                        class="form-control form-control-sm" 
                        @change="onFileSelected"
                      />
                    </div>
                    <div class="col-md-4">
                      <button
                        type="button"
                        class="btn btn-primary btn-sm w-100" 
                        @click="uploadImagesWithMetadata"
                        :disabled="!hasSelectedFiles || uploadProgress > 0"
                      >
                        <i class="material-icons-round me-1 text-xs">upload</i>
                        업로드
                      </button>
                    </div>
                  </div>
                  
                  <!-- 두 번째 줄: 태그 입력 -->
                  <div class="row g-2">
                    <div class="col-12">
                      <input 
                        v-model="imageMetadata.tags"
                        type="text" 
                        class="form-control form-control-sm" 
                        placeholder="태그 입력 : 문제, 도표, 수학 (쉼표로 구분)"
                      />
                    </div>
                  </div>
                </div>
              </div>

            <!-- 업로드 진행 상태 -->
            <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
              <div class="progress progress-sm">
                <div 
                  class="progress-bar bg-gradient-primary" 
                  role="progressbar" 
                  :style="`width: ${uploadProgress}%`" 
                  :aria-valuenow="uploadProgress" 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                </div>
              </div>
              <small class="text-muted">이미지 업로드 중... {{ uploadProgress }}%</small>
            </div>

            <!-- 이미지 검색 -->
            <div class="search-container mb-4">
              <div class="search-form">
                <div class="row g-2 align-items-center">
                  <div class="col-auto">
                    <div class="search-icon">
                      <i class="material-icons-round">search</i>
                    </div>
                  </div>
                  <div class="col">
                                         <input 
                       v-model="imageSearchQuery"
                       type="text"
                       class="form-control form-control-sm search-input"
                       placeholder="태그로 이미지 검색 (예: 문제, 도표, 수학, 영어)..."
                       @keyup.enter="searchImages"
                     >
                  </div>
                  <div class="col-auto">
                                         <button 
                       class="btn btn-primary btn-sm search-btn"
                       @click="searchImages"
                       :disabled="loading"
                       style="margin-top: 10px;"
                     >
                                             <span v-if="loading">
                         <span class="spinner-border spinner-border-sm me-2"></span>
                         검색 중...
                       </span>
                      <span v-else>
                        <i class="material-icons-round me-1 text-xs">search</i>
                        검색
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 이미지 갤러리 -->
            <div class="mt-4">
              <h6 class="text-uppercase text-sm font-weight-bolder mb-3">
                <i class="material-icons-round text-sm me-1">photo_library</i>
                이미지 목록
                <span v-if="images.length > 0" class="badge bg-info ms-2">{{ images.length }}개</span>
              </h6>
              
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">이미지를 불러오는 중...</p>
              </div>
              
              <div v-else-if="images.length === 0" class="text-center py-5">
                <i class="material-icons text-6xl text-secondary mb-3">photo</i>
                <h6 class="text-secondary">업로드된 이미지가 없습니다</h6>
                <p class="text-muted">위의 업로드 영역을 사용하여 이미지를 추가해보세요.</p>
              </div>
              
              <div v-else class="row">
                <div 
                  v-for="(image, index) in images" 
                  :key="index" 
                  class="col-md-3 col-sm-4 col-6 mb-4"
                >
                  <div class="card image-card h-100">
                    <div class="image-container">
                      <img :src="image.url" class="card-img-top" :alt="image.filename" @error="handleImageError">
                      <div class="image-overlay">
                        <div class="btn-group" role="group">
                          <button 
                            type="button"
                            class="btn btn-sm btn-info" 
                            @click="copyImageUrl(image.filename)"
                            title="URL 복사"
                          >
                            <i class="material-icons-round text-xs">content_copy</i>
                          </button>
                          <button 
                            type="button"
                            class="btn btn-sm btn-warning" 
                            @click="openEditTags(image)"
                            title="태그 수정"
                          >
                            <i class="material-icons-round text-xs">edit</i>
                          </button>
                          <button 
                            type="button"
                            class="btn btn-sm btn-danger" 
                            @click="deleteImage(image.filename)"
                            title="삭제"
                          >
                            <i class="material-icons-round text-xs">delete</i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="card-footer p-2">
                      <p class="text-sm mb-1 text-truncate" :title="image.filename">{{ image.filename }}</p>
                      <small class="text-muted" v-if="image.file_size">{{ formatFileSize(image.file_size) }}</small>
                      <div v-if="image.tags && image.tags.length > 0" class="mt-1">
                        <span 
                          v-for="tag in image.tags" 
                          :key="tag" 
                          class="badge bg-info me-1 text-xs"
                        >
                          {{ tag }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>

            <!-- 오디오 탭 -->
            <div v-show="activeTab === 'audio'">
              <!-- 오디오 업로드 섹션 -->
              <div class="upload-section mb-3">
                <div class="upload-header">
                  <div class="upload-content">
                    <h6 class="upload-title">오디오 업로드 (MP3, WAV, OGG, M4A, AAC, FLAC 지원)</h6>
                  </div>
                </div>
                
                <div class="upload-form">
                  <!-- 첫 번째 줄: 파일 선택 + 업로드 버튼 -->
                  <div class="row g-2 align-items-center mb-2">
                    <div class="col-md-8">
                      <input 
                        type="file" 
                        ref="audioFileInput" 
                        multiple 
                        accept="audio/*" 
                        class="form-control form-control-sm" 
                        @change="onAudioFileSelected"
                      />
                    </div>
                    <div class="col-md-4">
                      <button
                        type="button"
                        class="btn btn-warning btn-sm w-100" 
                        @click="uploadAudioWithMetadata"
                        :disabled="!hasSelectedAudioFiles || audioUploadProgress > 0"
                      >
                        <i class="material-icons-round me-1 text-xs">upload</i>
                        업로드
                      </button>
                    </div>
                  </div>
                  
                  <!-- 두 번째 줄: 스크립트 입력 -->
                  <div class="row g-2">
                    <div class="col-12">
                      <textarea 
                        v-model="audioMetadata.script"
                        class="form-control form-control-sm" 
                        rows="2"
                        placeholder="스크립트 입력 : 오디오 내용 요약이나 대본을 입력하세요"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 오디오 업로드 진행 상태 -->
              <div v-if="audioUploadProgress > 0 && audioUploadProgress < 100" class="upload-progress">
                <div class="progress progress-sm">
                  <div 
                    class="progress-bar bg-gradient-warning" 
                    role="progressbar" 
                    :style="`width: ${audioUploadProgress}%`" 
                    :aria-valuenow="audioUploadProgress" 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                  </div>
                </div>
                <small class="text-muted">오디오 업로드 중... {{ audioUploadProgress }}%</small>
              </div>

              <!-- 오디오 검색 -->
              <div class="search-container mb-4">
                <div class="search-form">
                  <div class="row g-2 align-items-center">
                    <div class="col-auto">
                      <div class="search-icon audio-search-icon">
                        <i class="material-icons-round">search</i>
                      </div>
                    </div>
                    <div class="col">
                                           <input 
                       v-model="audioSearchQuery"
                       type="text"
                       class="form-control form-control-sm search-input"
                       placeholder="스크립트로 오디오 검색 (예: 영어 듣기, 수학 문제 해설, 과학 실험)..."
                       @keyup.enter="searchAudioFiles"
                     >
                    </div>
                    <div class="col-auto">
                      <button 
                        class="btn btn-warning btn-sm search-btn"
                        @click="searchAudioFiles"
                        :disabled="audioLoading"
                        style="margin-top: 10px;"
                      >
                        <span v-if="audioLoading">
                          <span class="spinner-border spinner-border-sm me-2"></span>
                          검색 중...
                        </span>
                        <span v-else>
                          <i class="material-icons-round me-1 text-xs">search</i>
                          검색
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 오디오 파일 목록 -->
              <div class="mt-4">
                <h6 class="text-uppercase text-sm font-weight-bolder mb-3">
                  <i class="material-icons-round text-sm me-1">library_music</i>
                  오디오 목록
                  <span v-if="audioFiles.length > 0" class="badge bg-warning ms-2">{{ audioFiles.length }}개</span>
                </h6>
                
                <div v-if="audioLoading" class="text-center py-5">
                  <div class="spinner-border text-warning" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-2">오디오를 불러오는 중...</p>
                </div>
                
                <div v-else-if="audioFiles.length === 0" class="text-center py-5">
                  <i class="material-icons text-6xl text-secondary mb-3">audiotrack</i>
                  <h6 class="text-secondary">업로드된 오디오가 없습니다</h6>
                  <p class="text-muted">위의 업로드 영역을 사용하여 오디오를 추가해보세요.</p>
                </div>
                
                <div v-else class="row">
                  <div 
                    v-for="(audio, index) in audioFiles" 
                    :key="index" 
                    class="col-md-6 col-12 mb-3"
                  >
                    <div class="card audio-card h-100">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="audio-icon me-3" style="width: 48px; height: 48px;">
                            <i class="material-icons-round text-warning" style="font-size: 32px;">audiotrack</i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="card-title mb-1 text-truncate" :title="audio.filename">{{ audio.filename }}</h6>
                            <small class="text-muted" v-if="audio.file_size">{{ formatFileSize(audio.file_size) }}</small>
                            <div v-if="audio.script" class="mt-1">
                              <small class="text-muted d-block">
                                <i class="material-icons-round text-xs me-1">description</i>
                                {{ audio.script.length > 50 ? audio.script.substring(0, 50) + '...' : audio.script }}
                              </small>
                            </div>
                            <div class="mt-2">
                              <button 
                                type="button"
                                class="btn btn-sm btn-outline-primary me-2" 
                                @click="playAudio(audio.filename)"
                                :disabled="currentPlayingAudio === audio.filename"
                              >
                                <i class="material-icons-round text-xs">{{ currentPlayingAudio === audio.filename ? 'stop' : 'play_arrow' }}</i>
                                {{ currentPlayingAudio === audio.filename ? '정지' : '재생' }}
                              </button>
                              <button 
                                type="button"
                                class="btn btn-sm btn-outline-warning me-2" 
                                @click="openEditScript(audio)"
                                title="스크립트 수정"
                              >
                                <i class="material-icons-round text-xs">edit</i>
                              </button>
                              <button 
                                type="button"
                                class="btn btn-sm btn-outline-info me-2" 
                                @click="copyAudioUrl(audio.filename)"
                                title="파일명 복사"
                              >
                                <i class="material-icons-round text-xs">content_copy</i>
                              </button>
                              <button 
                                type="button"
                                class="btn btn-sm btn-outline-danger" 
                                @click="deleteAudio(audio.filename)"
                                title="삭제"
                              >
                                <i class="material-icons-round text-xs">delete</i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary btn-sm" @click="closeModal">
            <i class="material-icons-round me-1">close</i>
            닫기
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- 메타데이터 수정 모달: 태그 -->
  <div v-if="showEditTagsModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.3);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">태그 수정 - {{ editTagsTarget ? editTagsTarget.filename : '' }}</h6>
          <button type="button" class="btn-close" @click="showEditTagsModal = false"></button>
        </div>
        <div class="modal-body">
          <input v-model="editTagsInput" type="text" class="form-control form-control-sm" placeholder="쉼표로 구분하여 입력" />
          <small class="text-muted d-block mt-2">예: 문제, 도표, 수학</small>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary btn-sm" @click="showEditTagsModal = false">취소</button>
          <button class="btn btn-primary btn-sm" @click="saveEditTags">저장</button>
        </div>
      </div>
    </div>
  </div>

  <!-- 메타데이터 수정 모달: 스크립트 -->
  <div v-if="showEditScriptModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.3);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">스크립트 수정 - {{ editScriptTarget ? editScriptTarget.filename : '' }}</h6>
          <button type="button" class="btn-close" @click="showEditScriptModal = false"></button>
        </div>
        <div class="modal-body">
          <textarea v-model="editScriptInput" class="form-control form-control-sm" rows="4" placeholder="스크립트를 입력하세요"></textarea>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary btn-sm" @click="showEditScriptModal = false">취소</button>
          <button class="btn btn-warning btn-sm" @click="saveEditScript">저장</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ExamFilesModal",
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      activeTab: 'images',
      images: [],
      audioFiles: [],
      uploadProgress: 0,
      audioUploadProgress: 0,
      loading: false,
      audioLoading: false,
      currentPlayingAudio: null,
      currentAudio: null,
      selectedFiles: null,
      selectedAudioFiles: null,
      imageMetadata: {
        tags: ''
      },
      audioMetadata: {
        script: ''
      },
      // 검색 관련 변수들
      imageSearchQuery: '',
      audioSearchQuery: ''
      ,
      // 메타데이터 수정용 상태
      showEditTagsModal: false,
      editTagsTarget: null,
      editTagsInput: '',
      showEditScriptModal: false,
      editScriptTarget: null,
      editScriptInput: ''
    };
  },
  computed: {
    hasSelectedFiles() {
      return this.selectedFiles && this.selectedFiles.length > 0 && this.imageMetadata.tags.trim() !== '';
    },
    hasSelectedAudioFiles() {
      return this.selectedAudioFiles && this.selectedAudioFiles.length > 0 && this.audioMetadata.script.trim() !== '';
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchImages();
        this.fetchAudioFiles();
      }
    }
  },
  beforeUnmount() {
    // 컴포넌트 소멸 시 오디오 정리
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
  },
  methods: {
    closeModal() {
      // 모달 닫을 때 오디오 정리
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      this.currentPlayingAudio = null;
      this.$emit('close');
    },
    
    async fetchImages() {
      this.loading = true;
      try {
        const params = {};
        if (this.imageSearchQuery.trim()) {
          params.tags = this.imageSearchQuery.trim();
        }
        
        const res = await this.$axios({
          method: "get",
          url: `/exam-files/imageStorage`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: false,
          params
        });
        
        if (res.status === 200) {
          this.images = res.data.map(img => ({
            ...img,
            url: img.url || `${this.$CONF.ACHIEVE_API_URL}/files/image/stream/${img.filename}?file_type=exam_files`
          }));
        }
      } catch (error) {
        console.error("Error fetching images:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "이미지를 불러오는 중 오류가 발생했습니다." 
        });
      } finally {
        this.loading = false;
      }
    },

    openEditTags(image) {
      this.editTagsTarget = image;
      const currentTags = Array.isArray(image.tags) ? image.tags.join(', ') : (image.tags || '');
      this.editTagsInput = currentTags;
      this.showEditTagsModal = true;
    },

    async saveEditTags() {
      if (!this.editTagsTarget) return;
      try {
        const formData = new FormData();
        formData.append('tags', this.editTagsInput);
        const res = await this.$axios({
          method: 'put',
          url: `/exam-files/files/metadata/${this.editTagsTarget.filename}/tags`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          data: formData
        });

        if (res.status === 200) {
          this.$notifyVue({ type: 'success', title: '성공', text: '태그가 업데이트되었습니다.' });
          this.showEditTagsModal = false;
          this.editTagsTarget = null;
          this.editTagsInput = '';
          this.fetchImages();
        }
      } catch (e) {
        console.error('saveEditTags error', e);
        this.$notifyVue({ type: 'error', title: '오류', text: '태그 업데이트 중 오류가 발생했습니다.' });
      }
    },

    openEditScript(audio) {
      this.editScriptTarget = audio;
      this.editScriptInput = audio.script || '';
      this.showEditScriptModal = true;
    },

    async saveEditScript() {
      if (!this.editScriptTarget) return;
      try {
        const formData = new FormData();
        formData.append('script', this.editScriptInput);
        const res = await this.$axios({
          method: 'put',
          url: `/exam-files/files/metadata/${this.editScriptTarget.filename}/script`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          data: formData
        });

        if (res.status === 200) {
          this.$notifyVue({ type: 'success', title: '성공', text: '스크립트가 업데이트되었습니다.' });
          this.showEditScriptModal = false;
          this.editScriptTarget = null;
          this.editScriptInput = '';
          this.fetchAudioFiles();
        }
      } catch (e) {
        console.error('saveEditScript error', e);
        this.$notifyVue({ type: 'error', title: '오류', text: '스크립트 업데이트 중 오류가 발생했습니다.' });
      }
    },

    async fetchAudioFiles() {
      this.audioLoading = true;
      try {
        const params = {};
        if (this.audioSearchQuery.trim()) {
          params.script = this.audioSearchQuery.trim();
        }
        
        const res = await this.$axios({
          method: "get",
          url: `/exam-files/audioStorage`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: false,
          params
        });
        
        if (res.status === 200) {
          this.audioFiles = res.data.map(audio => ({
            ...audio,
            url: audio.url || `${this.$CONF.ACHIEVE_API_URL}/files/audio/stream/${audio.filename}?file_type=exam_files`
          }));
        }
      } catch (error) {
        console.error("Error fetching audio files:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "오디오를 불러오는 중 오류가 발생했습니다." 
        });
      } finally {
        this.audioLoading = false;
      }
    },
    // 한글 파일명 검증 및 알림
    validateFilenames(files, fileType = '파일') {
      const koreanFiles = files.filter(file => this.$hasKoreanInFilename(file.name));
      if (koreanFiles.length > 0) {
        const fileNames = koreanFiles.map(file => file.name).join(', ');
        this.$notifyVue({
          title: '파일명 오류',
          text: `${fileType} 파일명에 한글이 포함되어 있습니다.\n영문, 숫자, 특수문자만 사용해주세요.\n\n문제 파일: ${fileNames}`,
          type: 'error',
          duration: 5000
        });
        return false;
      }
      return true;
    },
    
    onFileSelected(e) {
      const files = Array.from(e.target.files);
      
      // 한글 파일명 검증
      if (!this.validateFilenames(files, '이미지')) {
        // 파일 입력 초기화
        e.target.value = '';
        this.selectedFiles = [];
        return;
      }
      
      this.selectedFiles = files;
    },

    async uploadImagesWithMetadata() {
      if (!this.selectedFiles || this.selectedFiles.length === 0) {
        this.$notifyVue({ 
          type: "warning", 
          title: "경고", 
          text: "업로드할 이미지 파일을 선택해주세요." 
        });
        return;
      }

      const imageFiles = Array.from(this.selectedFiles).filter(file => 
        file.type.startsWith('image/')
      );
      
      if (imageFiles.length === 0) {
        this.$notifyVue({ 
          type: "warning", 
          title: "경고", 
          text: "이미지 파일만 업로드할 수 있습니다." 
        });
        return;
      }

      this.uploadProgress = 1;
      
      try {
        for (const file of imageFiles) {
          await this.uploadSingleImageWithMetadata(file);
        }
        
        this.$notifyVue({ 
          type: "success", 
          title: "성공", 
          text: "이미지가 성공적으로 업로드되었습니다." 
        });
        this.fetchImages();
        this.resetImageForm();
      } catch (error) {
        console.error("Image upload error:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "이미지 업로드 중 오류가 발생했습니다." 
        });
      } finally {
        this.uploadProgress = 0;
      }
    },

    async uploadSingleImageWithMetadata(file) {
      const formData = new FormData();
      formData.append('file', file);
      
      // 메타데이터 추가
      if (this.imageMetadata.tags.trim()) {
        const tags = this.imageMetadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        formData.append('tags', JSON.stringify(tags));
      }
      
      const res = await this.$axios({
        method: "post",
        url: `/exam-files/upload/image`,
        api_url: this.$CONF.ACHIEVE_API_URL,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
        }
      });
      
      return res;
    },

    resetImageForm() {
      this.selectedFiles = null;
      this.imageMetadata.tags = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    

    
    async deleteImage(filename) {
      if (!confirm("정말로 이 이미지를 삭제하시겠습니까?")) {
        return;
      }
      
      try {
        const res = await this.$axios({
          method: "delete",
          url: `/exam-files/image/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: true,
        });
        
        if (res.status === 200 || res.status === 204) {
          this.$notifyVue({ 
            type: "success", 
            title: "성공", 
            text: "이미지가 삭제되었습니다." 
          });
          this.fetchImages();
        }
      } catch (error) {
        console.error("Error deleting image:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "이미지 삭제 중 오류가 발생했습니다." 
        });
      }
    },

    // 오디오 관련 메서드들
    onAudioFileSelected(e) {
      const files = Array.from(e.target.files);
      
      // 한글 파일명 검증
      if (!this.validateFilenames(files, '오디오')) {
        // 파일 입력 초기화
        e.target.value = '';
        this.selectedAudioFiles = [];
        return;
      }
      
      this.selectedAudioFiles = files;
    },

    async uploadAudioWithMetadata() {
      if (!this.selectedAudioFiles || this.selectedAudioFiles.length === 0) {
        this.$notifyVue({ 
          type: "warning", 
          title: "경고", 
          text: "업로드할 오디오 파일을 선택해주세요." 
        });
        return;
      }

      const audioFiles = Array.from(this.selectedAudioFiles).filter(file => 
        file.type.startsWith('audio/')
      );
      
      if (audioFiles.length === 0) {
        this.$notifyVue({ 
          type: "warning", 
          title: "경고", 
          text: "오디오 파일만 업로드할 수 있습니다." 
        });
        return;
      }

      this.audioUploadProgress = 1;
      
      try {
        for (const file of audioFiles) {
          await this.uploadSingleAudioWithMetadata(file);
        }
        
        this.$notifyVue({ 
          type: "success", 
          title: "성공", 
          text: "오디오가 성공적으로 업로드되었습니다." 
        });
        this.fetchAudioFiles();
        this.resetAudioForm();
      } catch (error) {
        console.error("Audio upload error:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "오디오 업로드 중 오류가 발생했습니다." 
        });
      } finally {
        this.audioUploadProgress = 0;
      }
    },

    async uploadSingleAudioWithMetadata(file) {
      const formData = new FormData();
      formData.append('file', file);
      
      // 메타데이터 추가
      if (this.audioMetadata.script.trim()) {
        formData.append('script', this.audioMetadata.script.trim());
      }
      
      const res = await this.$axios({
        method: "post",
        url: `/exam-files/upload/audio`,
        api_url: this.$CONF.ACHIEVE_API_URL,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          this.audioUploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
        }
      });
      
      return res;
    },

    resetAudioForm() {
      this.selectedAudioFiles = null;
      this.audioMetadata.script = '';
      if (this.$refs.audioFileInput) {
        this.$refs.audioFileInput.value = '';
      }
    },



    async deleteAudio(filename) {
      if (!confirm("정말로 이 오디오를 삭제하시겠습니까?")) {
        return;
      }
      
      try {
        const res = await this.$axios({
          method: "delete",
          url: `/exam-files/audio/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: true,
        });
        
        if (res.status === 200 || res.status === 204) {
          this.$notifyVue({ 
            type: "success", 
            title: "성공", 
            text: "오디오가 삭제되었습니다." 
          });
          this.fetchAudioFiles();
        }
      } catch (error) {
        console.error("Error deleting audio:", error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "오디오 삭제 중 오류가 발생했습니다." 
        });
      }
    },

    async playAudio(filename) {
      if (this.currentPlayingAudio === filename) {
        // 현재 재생 중인 오디오 정지
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
          this.currentPlayingAudio = null;
        }
        return;
      }

      // 기존 오디오 정지
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }

      try {
        this.currentPlayingAudio = filename;

        // 인증된 요청으로 오디오 데이터를 가져와서 Blob URL 생성
        const response = await this.$axios({
          method: 'get',
          url: `/files/audio/stream/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          params: { file_type: 'exam_files' },
          responseType: 'blob',
          loadingBar: false
        });

        if (response.status === 200) {
          // Blob을 URL로 변환
          const audioBlob = response.data;
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Audio 객체 생성 및 재생
          this.currentAudio = new Audio(audioUrl);
          
          // 이벤트 리스너 추가
          this.currentAudio.addEventListener('ended', () => {
            URL.revokeObjectURL(audioUrl); // 메모리 해제
            this.currentPlayingAudio = null;
            this.currentAudio = null;
          });

          this.currentAudio.addEventListener('error', (e) => {
            console.error('Audio playback error:', e);
            URL.revokeObjectURL(audioUrl); // 메모리 해제
            this.$notifyVue({ 
              type: "error", 
              title: "오류", 
              text: "오디오 재생 중 오류가 발생했습니다." 
            });
            this.currentPlayingAudio = null;
            this.currentAudio = null;
          });

          // 재생 시작
          await this.currentAudio.play();
        }
      } catch (error) {
        console.error('Audio load error:', error);
        this.$notifyVue({ 
          type: "error", 
          title: "오류", 
          text: "오디오 파일을 불러오는 중 오류가 발생했습니다." 
        });
        this.currentPlayingAudio = null;
        this.currentAudio = null;
      }
    },

    copyAudioUrl(filename) {
      navigator.clipboard.writeText(filename)
        .then(() => {
          this.$notifyVue({ 
            type: "success", 
            title: "성공", 
            text: "파일명이 클립보드에 복사되었습니다." 
          });
        })
        .catch(err => {
          console.error('Failed to copy filename: ', err);
          this.$notifyVue({ 
            type: "error", 
            title: "오류", 
            text: "파일명 복사에 실패했습니다." 
          });
        });
    },
    
    copyImageUrl(url) {
      navigator.clipboard.writeText(url)
        .then(() => {
          this.$notifyVue({ 
            type: "success", 
            title: "성공", 
            text: "URL이 클립보드에 복사되었습니다." 
          });
        })
        .catch(err => {
          console.error('Failed to copy URL: ', err);
          this.$notifyVue({ 
            type: "error", 
            title: "오류", 
            text: "URL 복사에 실패했습니다." 
          });
        });
    },
    
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // 검색 관련 메서드들
    searchImages() {
      this.fetchImages();
    },

    searchAudioFiles() {
      this.fetchAudioFiles();
    }
  }
};
</script>

<style scoped>
.upload-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e0e6ed;
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.upload-section:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.upload-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.upload-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.upload-icon.audio-icon {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
}

.upload-icon i {
  color: white;
  font-size: 20px;
}

.upload-content {
  flex: 1;
}

.upload-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
}

.upload-desc {
  margin: 0;
  font-size: 0.75rem;
  color: #6c757d;
}

.upload-form {
  background: #ffffff;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e0e6ed;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-label.text-sm {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.form-control-sm {
  font-size: 0.8rem;
  padding: 0.375rem 0.75rem;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.form-control-sm:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
  outline: none;
}

.form-control-sm:hover:not(:focus) {
  border-color: #adb5bd;
}

.upload-progress {
  background: rgba(255, 255, 255, 0.9);
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  margin-bottom: 1rem;
}

.progress-sm {
  height: 6px;
  margin-bottom: 0.5rem;
}

.progress-sm .progress-bar {
  border-radius: 3px;
}

.image-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e6ed;
  border-radius: 8px;
  overflow: hidden;
}

.image-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.image-container {
  position: relative;
  overflow: hidden;
  height: 150px;
  background: #f8f9fa;
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-container:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.modal-dialog {
  max-width: 900px;
  width: 90%;
}

/* 검색 컨테이너 스타일 */
.search-container {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e0e6ed;
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.search-container:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.search-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.search-icon.audio-search-icon {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.search-icon i {
  color: white;
  font-size: 18px;
}

.search-form {
  background: #ffffff;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #e0e6ed;
}

.search-input {
  border: 2px solid #dee2e6;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

.search-btn {
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
  height: calc(1.5em + 0.5rem + 2px); /* form-control-sm과 정확히 동일한 높이 */
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.modal-body {
  max-height: 65vh;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
    width: calc(100% - 1rem);
    max-width: none;
  }
  
  .modal-body {
    max-height: 60vh;
  }
  
  .col-6 {
    margin-bottom: 1rem;
  }
}

@media (max-width: 992px) {
  .modal-dialog {
    max-width: 800px;
  }
}

/* 탭 스타일 */
.nav-tabs .nav-link {
  border: none;
  border-radius: 8px 8px 0 0;
  margin-right: 8px;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  border-bottom: 1px solid #f8f9fa;
}

.nav-tabs .nav-link:hover {
  color: #495057;
  background-color: #f8f9fa;
}

/* 오디오 카드 스타일 */
.audio-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e6ed;
  border-radius: 8px;
}

.audio-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.audio-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}
</style>
